import { type HTMLAttributes } from "react";
import { cn } from "@/shared/lib/cn";
import { IconButton } from "./IconButton";
import { MinusIcon, PlusIcon } from "./icons";

export interface NumberStepperProps extends Omit<HTMLAttributes<HTMLDivElement>, "onChange"> {
  label: string;
  value: number;
  onValueChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  values?: readonly number[];
  decreaseLabel?: string;
  increaseLabel?: string;
  disabled?: boolean;
}

/** Bounded counts, including non-contiguous values admitted by a model. */
export function NumberStepper({ label, value, onValueChange, min = 1, max = 10,
  step = 1, values, decreaseLabel = `Decrease ${label.toLowerCase()}`,
  increaseLabel = `Increase ${label.toLowerCase()}`, disabled, className, ...props
}: NumberStepperProps) {
  const allowed = values ? [...new Set(values)].filter(Number.isFinite).sort((a, b) => a - b) : undefined;
  const previous = allowed ? allowed.filter((item) => item < value).at(-1) : value > min ? Math.max(min, value - step) : undefined;
  const next = allowed ? allowed.find((item) => item > value) : value < max ? Math.min(max, value + step) : undefined;
  return <div {...props} role="group" aria-label={label} className={cn("inline-flex items-center gap-1", className)}>
    <IconButton size="sm" aria-label={decreaseLabel} disabled={disabled || previous === undefined}
      onClick={() => { if (previous !== undefined) onValueChange(previous); }}><MinusIcon /></IconButton>
    <output aria-label={label} className="min-w-7 text-center text-sm font-medium tabular-nums text-ink" aria-live="polite">{value}</output>
    <IconButton size="sm" aria-label={increaseLabel} disabled={disabled || next === undefined}
      onClick={() => { if (next !== undefined) onValueChange(next); }}><PlusIcon /></IconButton>
  </div>;
}
